import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  ChevronRight,
  ExternalLink,
  Globe,
  Instagram,
  MapPin,
  Phone,
  Sparkles,
  Facebook,
  Youtube,
} from 'lucide-react';
import SEO from './SEO';
import { useManagedCollection } from '../admin/useManagedCollection';
import { KIDS_ENTRIES, kidsSlug } from './Kids';

const BASE_URL = 'https://dallasdesiscoop.com';

const FALLBACK_IMAGE =
  'https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?w=600&h=400&fit=crop';

// Collect every contact/social URL a listing has, deduped, with a label and
// icon — mirrors the button logic on the grid cards.
function collectContacts(e: any) {
  const candidates: { url?: string; label?: string }[] = [
    { url: e.website, label: 'Website' },
    { url: e.instagram, label: 'Instagram' },
    { url: e.facebook, label: 'Facebook' },
    { url: e.youtube, label: 'YouTube' },
    { url: e.tiktok, label: 'TikTok' },
  ];
  const seen = new Set<string>();
  return candidates
    .filter((c): c is { url: string; label?: string } => !!c.url && c.url !== '#')
    .filter((c) => (seen.has(c.url) ? false : (seen.add(c.url), true)))
    .map((c) => {
      const u = c.url.toLowerCase();
      if (u.includes('facebook.com')) return { ...c, Icon: Facebook, label: 'Facebook' };
      if (u.includes('youtube.com') || u.includes('youtu.be')) return { ...c, Icon: Youtube, label: 'YouTube' };
      if (u.includes('instagram.com')) return { ...c, Icon: Instagram, label: 'Instagram' };
      return { ...c, Icon: Globe, label: c.label || 'Visit Page' };
    });
}

function mapsUrl(e: any) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${e.name}, ${e.address || 'Dallas, TX'}`)}`;
}

export default function KidsDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const mc = useManagedCollection<any>('kids', KIDS_ENTRIES, (e, i) => kidsSlug(e, i));

  const entry = mc.items.find((e: any, i: number) => kidsSlug(e, i) === slug);

  if (mc.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-[#082f6d] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!entry) {
    return (
      <div
        className="min-h-screen bg-white flex flex-col items-center justify-center px-4"
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        <SEO
          title="Kids Listing Not Found | Dallas Desi Scoop"
          description="This kids activity and class listing could not be found."
          canonical="/kids"
          noindex
        />
        <p className="text-gray-500 text-lg mb-4">Kids listing not found.</p>
        <Link to="/kids" className="text-[#082f6d] font-semibold hover:underline">
          ← Back to Kids Activities &amp; Classes
        </Link>
      </div>
    );
  }

  const name = entry.name || 'Kids Activity';
  const image = entry.image || FALLBACK_IMAGE;
  const title = (entry.title || '').trim();
  const type = (entry.type || '').trim();
  const city = (entry.city || '').trim();
  const phone = (entry.phone || '').trim();
  const address = (entry.address || '').trim();
  const contacts = collectContacts(entry);
  const canonicalPath = `/kids/${slug}`;
  const detailDescription = String(entry.detailDescription || '').trim();

  // Data-driven schema type: day care listings get ChildCare, everything
  // else gets LocalBusiness.
  const isDayCare = /day\s*care|daycare|montessori/i.test(`${type} ${name}`);
  const schemaType = isDayCare ? 'ChildCare' : 'LocalBusiness';

  const typeList = type
    ? type
        .split(',')
        .map((t: string) => t.trim())
        .filter(Boolean)
    : [];

  const pageTitle = title || (typeList.length > 0 ? typeList.join(' & ') : 'Kids Activities & Classes');
  const location = city || 'Dallas–Fort Worth';

  const seoTitle = `${name} | Kids Activities & Classes in Dallas–Fort Worth | Dallas Desi Scoop`;
  const metaDescription = detailDescription
    ? `${detailDescription.slice(0, 300)}${detailDescription.length > 300 ? '…' : ''}`
    : `${name}${pageTitle !== name ? ` — ${pageTitle}` : ''} serves families in ${location} and the wider Dallas–Fort Worth metroplex. Find address, phone, website, and program details on Dallas Desi Scoop.`;

  const jsonLd = [
    {
      '@type': schemaType,
      '@id': `${BASE_URL}${canonicalPath}#business`,
      name,
      image,
      description: detailDescription
        || `${name}${pageTitle !== name ? `, ${pageTitle}` : ''}, serving families in ${location} and the Dallas–Fort Worth metroplex.`,
      telephone: phone || undefined,
      url: contacts[0]?.url || `${BASE_URL}${canonicalPath}`,
      address: address
        ? {
            '@type': 'PostalAddress',
            streetAddress: address,
            addressRegion: 'TX',
            addressCountry: 'US',
          }
        : {
            '@type': 'PostalAddress',
            addressLocality: city || 'Dallas',
            addressRegion: 'TX',
            addressCountry: 'US',
          },
      areaServed: 'Dallas–Fort Worth metroplex',
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Kids Activities & Classes', item: `${BASE_URL}/kids` },
        { '@type': 'ListItem', position: 3, name, item: `${BASE_URL}${canonicalPath}` },
      ],
    },
  ];

  // Internal linking: other kids listings in DFW.
  const related = mc.items
    .map((e: any, i: number) => ({ entry: e, key: kidsSlug(e, i) }))
    .filter((r: any) => r.key !== slug)
    .slice(0, 6);

  return (
    <div className="min-h-screen bg-[#f9fafb]" style={{ fontFamily: "'Poppins', sans-serif" }}>
      <SEO
        title={seoTitle}
        description={metaDescription}
        canonical={canonicalPath}
        ogImage={image}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative w-full overflow-hidden" style={{ minHeight: '260px' }}>
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url("${image}")` }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to bottom, rgba(8,47,109,0.75) 0%, rgba(8,47,109,0.55) 55%, rgba(8,47,109,0.88) 100%)',
          }}
        />

        <div className="relative z-10 max-w-[1000px] mx-auto px-4 pt-6 pb-10 flex flex-col">
          <motion.div
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="self-start flex items-center gap-1.5 mb-5"
          >
            <Link
              to="/kids"
              className="flex items-center gap-1.5 text-white/80 hover:text-white text-[12px] font-medium transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to Kids Activities &amp; Classes
            </Link>
          </motion.div>

          <nav
            aria-label="Breadcrumb"
            className="flex items-center gap-1 text-[11px] text-white/70 mb-3 flex-wrap"
          >
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/kids" className="hover:text-white transition-colors">Kids Activities &amp; Classes</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white font-medium">{name}</span>
          </nav>

          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="text-white text-[26px] md:text-[34px] font-bold leading-tight"
            data-hs-id="kids-detail-h1"
          >
            {name}
          </motion.h1>

          {title && (
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.08 }}
              className="text-white/90 text-[13px] md:text-[15px] mt-2 max-w-[640px]"
            >
              {title}
            </motion.p>
          )}

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.14 }}
            className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-4 text-[12px] text-white/85"
          >
            {typeList.length > 0 && (
              <span className="flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-[#f59e0b]" /> {typeList.join(', ')}
              </span>
            )}
            {address && (
              <span className="flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-[#f59e0b]" /> {address}
              </span>
            )}
            {phone && (
              <span className="flex items-center gap-1.5">
                <Phone className="w-3.5 h-3.5 text-[#51ba4f]" /> {phone}
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-white/70" /> Serving Dallas–Fort Worth
            </span>
          </motion.div>
        </div>
      </section>

      {/* Body */}
      <section className="max-w-[1000px] w-[calc(100%-40px)] mx-auto px-4 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main column */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="bg-white rounded-xl border border-gray-100 shadow-sm p-6"
            >
              <h2 className="text-[18px] font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Sparkles className="w-4.5 h-4.5 text-[#082f6d]" /> About {name}
              </h2>
              {detailDescription ? (
                <p className="text-[14px] text-gray-600 leading-relaxed">{detailDescription}</p>
              ) : (
                <p className="text-[14px] text-gray-600 leading-relaxed">
                  {name}
                  {pageTitle !== name ? ` — ${pageTitle}` : ''} serves families in {location} and
                  across the Dallas–Fort Worth metroplex.
                </p>
              )}
              <p className="text-[14px] text-gray-600 leading-relaxed mt-3">
                Dallas Desi Scoop connects DFW Desi families with trusted kids' activities,
                classes, day care, tutoring, and party services. Contact {name} directly for
                program details, schedules, and enrollment information.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.08 }}
              className="bg-white rounded-xl border border-gray-100 shadow-sm p-6"
            >
              <h2 className="text-[18px] font-bold text-gray-900 mb-3">Activities &amp; Services</h2>
              {typeList.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {typeList.map((t: string) => (
                    <span
                      key={t}
                      className="text-[12px] font-medium text-[#082f6d] bg-blue-50 rounded-full px-3.5 py-1.5"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-[14px] text-gray-500">
                  [Coming soon] — activity details for this listing have not been added yet.
                </p>
              )}
              <p className="text-[13px] text-gray-500 mt-3">
                Call or visit the business directly for a full list of programs, pricing, and
                availability across Dallas, Plano, Frisco, Irving, McKinney, and the wider DFW
                area.
              </p>
            </motion.div>
          </div>

          {/* Sidebar */}
          <motion.aside
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.12 }}
            className="flex flex-col gap-6"
          >
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
              <h2 className="text-[16px] font-bold text-gray-900 mb-4">Contact &amp; Info</h2>
              <div className="flex flex-col gap-3">
                {phone && (
                  <a
                    href={`tel:${phone.replace(/[^\d+]/g, '')}`}
                    className="flex items-center justify-center gap-2 bg-[#51ba4f] hover:bg-[#459f43] text-white text-[13px] font-semibold py-2.5 px-4 rounded-lg transition-colors"
                  >
                    <Phone className="w-4 h-4" /> Call {phone}
                  </a>
                )}
                {entry.website && (
                  <a
                    href={/^https?:\/\//i.test(entry.website) ? entry.website : `https://${entry.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 bg-[#082f6d] hover:bg-[#062454] text-white text-[13px] font-semibold py-2.5 px-4 rounded-lg transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" /> Visit Website
                  </a>
                )}
                {contacts.map((c) => (
                  <a
                    key={c.url}
                    href={c.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center justify-center gap-2 text-[13px] font-semibold py-2.5 px-4 rounded-lg transition-colors ${
                      c.label === 'Instagram'
                        ? 'bg-gradient-to-br from-pink-500 to-orange-400 hover:from-pink-600 hover:to-orange-500 text-white'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                    }`}
                  >
                    <c.Icon className="w-4 h-4" /> {c.label}
                  </a>
                ))}
                {address && (
                  <a
                    href={mapsUrl(entry)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-[13px] font-semibold py-2.5 px-4 rounded-lg transition-colors"
                  >
                    <MapPin className="w-4 h-4" /> View on Google Maps
                  </a>
                )}
                {!phone && !entry.website && contacts.length === 0 && !address && (
                  <p className="text-[13px] text-gray-500">
                    [Coming soon] — contact details for this listing have not been added yet.
                  </p>
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
              <h2 className="text-[16px] font-bold text-gray-900 mb-3">Location</h2>
              {address ? (
                <>
                  <p className="text-[13px] text-gray-600 leading-relaxed">{address}</p>
                  <a
                    href={mapsUrl(entry)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#082f6d] hover:underline mt-2"
                  >
                    <MapPin className="w-3.5 h-3.5" /> Get Directions
                  </a>
                </>
              ) : (
                <p className="text-[13px] text-gray-500">
                  [Coming soon] — address for this listing has not been added yet.
                </p>
              )}
            </div>
          </motion.aside>
        </div>

        {/* Related listings */}
        {related.length > 0 && (
          <div className="mt-10">
            <h2 className="text-[18px] font-bold text-gray-900 mb-4">
              More Kids Activities &amp; Classes in DFW
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {related.map((r: any) => (
                <a
                  key={r.key}
                  href={`/kids/${r.key}`}
                  className="bg-white rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col group"
                >
                  <div className="w-full h-[110px] bg-gray-100 overflow-hidden">
                    <img
                      src={r.entry.image || FALLBACK_IMAGE}
                      alt={r.entry.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = FALLBACK_IMAGE;
                      }}
                    />
                  </div>
                  <div className="p-2.5">
                    <h3 className="text-[12px] font-bold text-[#082f6d] leading-tight group-hover:underline">
                      {r.entry.name}
                    </h3>
                    {r.entry.address && (
                      <p className="text-[10px] text-gray-500 mt-1 truncate">{r.entry.address}</p>
                    )}
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Internal links */}
        <div className="mt-10 bg-white rounded-xl border border-gray-100 shadow-sm p-6">
          <h2 className="text-[16px] font-bold text-gray-900 mb-3">Explore Dallas Desi Scoop</h2>
          <div className="flex flex-wrap gap-2">
            {[
              { to: '/restaurants', label: 'Indian Restaurants in DFW' },
              { to: '/events', label: 'Desi Events & Concerts' },
              { to: '/deals', label: 'Local Deals' },
              { to: '/temples', label: 'Temples in DFW' },
              { to: '/kids', label: 'All Kids Activities & Classes' },
            ].map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="text-[12px] font-medium text-[#082f6d] bg-blue-50 hover:bg-blue-100 rounded-full px-3.5 py-1.5 transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
